import { LexiconSlider } from '@dreamstack/feature-components/src/keenSlider/LexiconSlider/LexiconSlider'
import { MainLayout } from '@dreamstack/feature-components/src/layout/MainLayout'
import { lexiconOtherLocaleUrls } from '@dreamstack/feature-components/src/lexicon/getLexiconPath'
import { LexiconHeaderWithSearch } from '@dreamstack/feature-components/src/lexicon/LexiconHeaderWithSearch'
import { LexiconPreview } from '@dreamstack/feature-components/src/lexicon/LexiconPreview'
import type { LEXICON_LETTER_TYPE } from '@dreamstack/feature-components/src/lexicon/lexiconUrls.static'
import { LEXICON_LETTERS_LOWER } from '@dreamstack/feature-components/src/lexicon/lexiconUrls.static'
import {
  getStaticLexiconProps,
  StaticLexiconProps,
} from '@dreamstack/feature-components/src/lexicon/getStaticLexiconProps'
import {
  allLocales,
  OtherLocaleUrlsContextProvider,
  useLocale,
  useTranslation,
} from '@dreamstack/i18n'
import { SearchContextProvider } from '@dreamstack/search'
import { DreamstackSeo } from '@dreamstack/seo'
import { trpc } from '@dreamstack/trpc/client'
import flatMap from 'lodash/flatMap'
import map from 'lodash/map'
import { Fragment } from 'react'
import { getStaticPathsPlus } from '../../../../lib/getStaticPathsPlus'
import { getStaticPropsPlus } from '../../../../lib/getStaticPropsPlus'

export default function LexiconLetterPage(props?: StaticLexiconProps) {
  const lexiconLetter = props?.lexiconLetter
  const lettersWithContent = props?.lettersWithContent
  const t = useTranslation()
  const locale = useLocale()
  const title = t('accentro:propertyLexicon.title')

  const { data: articles } = trpc.cms.getLexiconArticlePreviews.useQuery(
    { lexiconLetter: lexiconLetter ?? undefined, locale },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
    }
  )
  const lexiconArticles = articles?.items

  return (
    <Fragment key={lexiconLetter}>
      <SearchContextProvider indexType={'lexicon'}>
        <OtherLocaleUrlsContextProvider context={lexiconOtherLocaleUrls}>
          <DreamstackSeo title={title} />
          <MainLayout
            navbarProps={{ forceDarkTextColor: true }}
            showContactFooter={true}
            showNewsletterFooter={true}
          >
            <LexiconHeaderWithSearch />
            <LexiconSlider
              activeLexiconLetter={lexiconLetter}
              lettersWithContent={lettersWithContent || []}
            />
            <div className="flex-col space-y-px-64 mt-px-64 md:grid md:grid-cols-2 md:space-y-0 md:gap-x-px-48 md:gap-y-px-80 md:mt-px-80 ml:gap-y-px-96 ml:mt-px-96">
              {map(lexiconArticles, (article, idx) => (
                <div key={idx}>
                  <LexiconPreview lexiconArticle={article} />
                </div>
              ))}
            </div>
          </MainLayout>
        </OtherLocaleUrlsContextProvider>
      </SearchContextProvider>
    </Fragment>
  )
}

export const getStaticProps = getStaticPropsPlus({
  getProps: getStaticLexiconProps,
})

export const getStaticPaths = getStaticPathsPlus({
  fallback: false,
  localizePaths: false,
  getPaths: async () => {
    return getStaticLexiconLetterPaths(LEXICON_LETTERS_LOWER)
  },
})

const getStaticLexiconLetterPaths = (lexiconLetter: LEXICON_LETTER_TYPE) => {
  return flatMap(allLocales, (locale) => {
    return map(lexiconLetter, (lexiconLetter) => {
      const lexiconLetterUrl = lexiconLetter
      return {
        params: {
          lexiconLetterUrl,
        },
        locale,
      }
    })
  })
}
